import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform, useInView } from "motion/react";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { AppIcon } from "../components/AppIcon";
import type { IconName } from "../assets/icons";
import {
  CheckCircle,
  CreditCard,
  DollarSign,
  FileText,
  Package,
  Truck,
  ArrowRight,
  Building2,
  TrendingUp,
  Camera,
  Bell,
  ClipboardList,
  AlertTriangle,
  Download,
  Archive,
  Trash2,
  Wrench,
  Search,
} from "lucide-react";

type KeyFeature = {
  icon: IconName;
  title: string;
  description: string;
};

const CounterAnimation = ({ value, duration = 2000 }: { value: number; duration?: number }) => {
  const [count, setCount] = useState(0);
  const ref = React.useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let startTime: number;
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * value));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, value, duration]);

  return <span ref={ref}>{count}</span>;
};

const StatusBadge = ({ status, delay = 0 }: { status: string; delay?: number }) => {
  const variants = {
    draft: "bg-gray-100 text-gray-800",
    scheduled: "bg-purple-100 text-purple-800",
    inprogress: "bg-yellow-100 text-yellow-800",
    completed: "bg-green-100 text-green-800",
    restocked: "bg-blue-100 text-blue-800",
  };

  const statusMap = {
    draft: "Draft",
    scheduled: "Scheduled",
    inprogress: "In Progress",
    completed: "Completed",
    restocked: "Restocked",
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay }}
      className={`px-3 py-1 rounded-full text-sm font-medium ${variants[status as keyof typeof variants] || variants.draft}`}
    >
      {statusMap[status as keyof typeof statusMap] || status}
    </motion.div>
  );
};

const DashboardMockup = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
    >
      <div className="bg-[#f5f5f7] px-5 py-3.5 border-b border-gray-200/80">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3.5 min-w-0">
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="w-3 h-3 rounded-full bg-[#ff5f57]"></div>
              <div className="w-3 h-3 rounded-full bg-[#febc2e]"></div>
              <div className="w-3 h-3 rounded-full bg-[#28c840]"></div>
            </div>
            <h3 className="font-medium text-gray-700 text-sm md:text-base truncate">
              Removal Order Dashboard
            </h3>
          </div>
          <div className="flex items-center gap-4 flex-shrink-0 text-gray-500">
            <Search className="w-4 h-4" />
            <Bell className="w-4 h-4" />
            <div className="w-7 h-7 rounded-full bg-gray-300"></div>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="text-2xl font-bold text-blue-600">
              <CounterAnimation value={18} />
            </div>
            <p className="text-sm text-gray-600">Active Removals</p>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-green-600">
              <CounterAnimation value={134} />
            </div>
            <p className="text-sm text-gray-600">Signs Recovered</p>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold" style={{ color: "#f58b01" }}>
              <CounterAnimation value={89} />%
            </div>
            <p className="text-sm text-gray-600">Reusable Condition</p>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-purple-600">
              $<CounterAnimation value={12450} />
            </div>
            <p className="text-sm text-gray-600">Cost Savings</p>
          </Card>
        </div>

        <div className="space-y-3">
          {[
            {
              address: "1234 Beverly Hills Dr",
              status: "scheduled",
              agent: "Sarah Johnson",
              condition: "Good",
            },
            {
              address: "5678 Manhattan Beach Ave",
              status: "inprogress",
              agent: "Mike Chen",
              condition: "Pickup in progress",
            },
            {
              address: "9101 Santa Monica Blvd",
              status: "completed",
              agent: "Lisa Rodriguez",
              condition: "Good - Restocked",
            },
            {
              address: "1213 Culver City St",
              status: "restocked",
              agent: "David Kim",
              condition: "Needs Repair",
            },
          ].map((job, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + index * 0.1 }}
              className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                <Trash2 className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">{job.address}</p>
                  <p className="text-sm text-gray-600">
                    {job.agent} • {job.condition}
                  </p>
                </div>
              </div>
              <StatusBadge status={job.status} delay={0.7 + index * 0.1} />
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const FeatureSection = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <motion.section
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8 }}
      className={`py-16 md:py-24 ${className}`}
    >
      {children}
    </motion.section>
  );
};

export default function RemovalOrdersLandingPage() {
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);

  const keyFeatures: KeyFeature[] = [
    {
      icon: "scheduleRemovalsAdvance",
      title: "Schedule Removals in Advance",
      description:
        "Agents and offices can schedule removals ahead of time based on listing status or sale completion. No missed pickups or back-and-forth — everything is planned and visible in advance.",
    },
    {
      icon: "knowConditionRestockedSigns",
      title: "Know the Condition of Restocked Signs",
      description:
        "After each removal, you'll see the condition of every returned sign — whether it's good, damaged, or needs repair — helping you decide what can be reused or replaced.",
    },
    {
      icon: "photoProofOfRemoval",
      title: "Photo Proof of Removal",
      description:
        "Get verified before-and-after photos for every removal. Reduce damage claims and ensure each pickup is completed as requested.",
    },
    {
      icon: "automaticInventoryRestocking",
      title: "Automatic Inventory Restocking",
      description:
        "Once a removal is confirmed, signs are automatically restocked and ready for future installations. Stay informed on every asset's status without manual updates.",
    },
    {
      icon: "detailedRemovalNotes",
      title: "Detailed Removal Notes",
      description:
        "Add special instructions for each pickup to ensure installers never miss details like access codes, property notes, or storage preferences.",
    },
    {
      icon: "flexiblePaymentsBilling",
      title: "Flexible Payments & Billing",
      description:
        "Bill agents directly or handle payments at the office level. Secure, PCI-compliant payment options ensure a smooth checkout process.",
    },
    {
      icon: "agentLevelTransparency",
      title: "Agent-Level Transparency",
      description:
        "View all removal requests tied to each agent with complete logs and real-time status updates. Managers and teams always stay aligned.",
    },
    {
      icon: "stayNotifiedStayInControl",
      title: "Stay Notified, Stay in Control",
      description:
        "Instant notifications keep managers and agents updated at every stage — from request to restocking.",
    },
    {
      icon: "removalMobileAppAccess",
      title: "Mobile App Access",
      description:
        "Create and track removal orders on the go. View photo proof, monitor progress, and get alerts when assets are restocked — all from your phone.",
    },
    {
      icon: "beyondSigns",
      title: "Beyond Signs",
      description:
        "RealEsy isn't just for signage — manage removals and restocking for lockboxes, riders, or any other field assets shared with vendors.",
    },
  ];

  const benefits: { icon: IconName; title: string; description: string }[] = [
    {
      icon: "removalSaveMoney",
      title: "Save Money",
      description: "Recover and reuse signs instead of buying new ones.",
    },
    {
      icon: "removalProtectAssets",
      title: "Protect Assets",
      description: "Track every sign through the removal process.",
    },
    {
      icon: "removalVerifyCompletion",
      title: "Verify Completion",
      description: "Photo proof ensures no signs are left behind.",
    },
    {
      icon: "removalImproveEfficiency",
      title: "Improve Efficiency",
      description: "Streamlined process reduces time and effort.",
    },
    {
      icon: "removalManageInventory",
      title: "Manage Inventory",
      description: "Automated updates keep inventory accurate.",
    },
  ];

  const faqs = [
    {
      q: "How do removal jobs save brokerages money?",
      a: "By tracking condition checks and restocking automatically, brokerages recover and reuse a majority of signs, reducing re-order costs.",
    },
    {
      q: "Can agents request removals directly?",
      a: "Yes. Managers can also create removals on behalf of agents for faster execution.",
    },
    {
      q: "Is proof of removal reliable?",
      a: "Every removal includes before/after photos with GPS stamps stored permanently in the cloud.",
    },
    {
      q: "What happens to damaged signs?",
      a: "Signs are categorized as Good, Needs Repair, or Damaged, allowing you to make informed decisions about replacement vs. repair.",
    },
    {
      q: "How does the condition check work?",
      a: "Installers assess each sign during pickup and upload photos showing the condition, which automatically updates your inventory.",
    },
    {
      q: "Can I track cost savings from removals?",
      a: "Yes, the dashboard shows total signs recovered, reuse rates, and estimated cost savings from avoiding new purchases.",
    },
  ];

  return (
    <>
      {/* Enhanced Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20 md:py-32 overflow-hidden">
        <motion.div style={{ y: backgroundY }} className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-200 rounded-full blur-3xl"></div>
        </motion.div>

        {/* Floating Icons */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[
            { Icon: Truck, x: "10%", y: "15%", delay: 0 },
            { Icon: Building2, x: "85%", y: "25%", delay: 1 },
            { Icon: FileText, x: "15%", y: "70%", delay: 2 },
            { Icon: Package, x: "80%", y: "65%", delay: 1.5 },
            { Icon: ClipboardList, x: "50%", y: "20%", delay: 0.5 },
            { Icon: CreditCard, x: "25%", y: "45%", delay: 2.5 },
            { Icon: Bell, x: "75%", y: "45%", delay: 1.2 },
            { Icon: DollarSign, x: "60%", y: "80%", delay: 0.8 },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 0.35, 0],
                scale: [0.8, 1.2, 0.8],
                y: [-20, 20, -20],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: item.delay,
                ease: "easeInOut",
              }}
              className="absolute"
              style={{ left: item.x, top: item.y }}
            >
              <item.Icon className="w-8 h-8 md:w-12 md:h-12 text-blue-500" />
            </motion.div>
          ))}
        </div>

        <div className="container mx-auto px-6 relative">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="max-w-6xl mx-auto space-y-8">
                <div className="space-y-4">
                  <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-blue-800 leading-tight">
                    Save Money, Recover Assets, and Track Every Removal with Full Transparency
                  </h1>
                </div>

                <div className="max-w-4xl mx-auto space-y-6">
                  <p className="text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed font-medium">
                    With RealEsy, brokerages gain a powerful way to manage signage removals — from
                    scheduling to inventory restocking — all in one streamlined platform.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center pt-8">
                  <Link to="/contact">
                    <Button
                      size="lg"
                      className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] transition-all duration-300 w-full sm:w-auto border-0"
                    >
                      Get Started
                      <ArrowRight className="ml-3 w-5 h-5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>

            <div className="max-w-5xl mx-auto">
              <DashboardMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Why Removal Jobs Matter Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Removal Jobs Matter for Brokerages
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
                Signs don't just need to go up — they also need to come down. Traditional removal
                processes often lead to lost, damaged, or forgotten signs, draining budgets and
                creating frustration for managers and agents alike.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="space-y-8"
              >
                <h3 className="text-2xl font-bold text-gray-900">
                  With RealEsy Removal Orders, you get:
                </h3>
                <div className="space-y-4">
                  {[
                    "Verified proof of every removal",
                    "Automated inventory updates",
                    "Clear visibility into which signs are reusable, damaged, or lost",
                    "Cost savings through better asset management",
                  ].map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 + index * 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700 text-lg">{benefit}</span>
                    </motion.div>
                  ))}
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.6 }}
                  className="text-white p-8 rounded-2xl"
                  style={{ background: "linear-gradient(90deg, #073399 0%, #236DD9 100%)" }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <DollarSign className="w-8 h-8" />
                    <h4 className="text-xl font-bold">Cost Savings Impact</h4>
                  </div>
                  <p className="text-lg leading-relaxed">
                    💡 Brokerages using RealEsy typically save 20–30% annually by reusing recovered
                    signage.
                  </p>
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="space-y-6"
              >
                {/* Enhanced Removal Workflow Illustration */}
                <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 p-8 rounded-2xl border border-gray-200 min-h-[500px]">
                  <div className="space-y-12">
                    {/* Step 1: Property with Large Real Estate Sign */}
                    <motion.div
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 }}
                      className="flex items-center gap-6"
                    >
                      <div className="relative">
                        <div className="w-20 h-20 bg-blue-600 rounded-2xl flex items-center justify-center relative overflow-hidden">
                          <Building2 className="w-10 h-10 text-white" />
                          <div className="absolute top-2 left-3 w-2 h-2 bg-blue-300 rounded-full"></div>
                          <div className="absolute top-2 right-3 w-2 h-2 bg-blue-300 rounded-full"></div>
                        </div>

                        <div className="absolute -bottom-4 -right-4 flex flex-col items-center">
                          <div className="w-8 h-2 bg-green-200 rounded-full opacity-70"></div>
                          <div className="w-2 h-12 bg-gray-700 rounded-full shadow-sm"></div>
                          <div className="w-12 h-8 bg-white border-3 border-blue-500 rounded-lg flex flex-col items-center justify-center shadow-lg transform -translate-y-2">
                            <div className="text-[10px] font-bold text-blue-500 text-center leading-tight">
                              <div>SOLD</div>
                            </div>
                            <div className="absolute top-1 right-1 w-full h-full bg-blue-200 rounded-lg -z-10"></div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white px-6 py-3 rounded-xl shadow-lg border-2 border-blue-600/20">
                        <p className="font-bold text-blue-600 text-lg">Removal Requested</p>
                        <p className="text-sm text-gray-600">Property sold - sign pickup needed</p>
                        <div className="flex items-center gap-2 mt-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-xs text-gray-500">456 Oak Avenue</span>
                        </div>
                      </div>
                    </motion.div>

                    {/* Flow Line 1 */}
                    <motion.div
                      initial={{ opacity: 0, scaleY: 0 }}
                      whileInView={{ opacity: 1, scaleY: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5, duration: 0.8 }}
                      className="flex justify-center"
                    >
                      <div className="w-1 h-8 bg-gradient-to-b from-blue-600 to-blue-300 rounded-full"></div>
                    </motion.div>

                    {/* Step 2: Truck Pickup */}
                    <motion.div
                      initial={{ opacity: 0, x: 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.7 }}
                      className="flex items-center gap-6 justify-end"
                    >
                      <div className="bg-white px-6 py-3 rounded-xl shadow-lg border-2 border-yellow-500/20">
                        <p className="font-bold text-yellow-600 text-lg">Pickup Scheduled</p>
                        <p className="text-sm text-gray-600">Installer en route</p>
                        <div className="flex items-center gap-2 mt-2">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                          <span className="text-xs text-gray-500">Dec 18, 11:00 AM</span>
                        </div>
                      </div>

                      <div className="relative">
                        <motion.div
                          animate={{ x: [-5, 5, -5] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                          className="w-20 h-20 bg-yellow-600 rounded-2xl flex items-center justify-center shadow-lg"
                        >
                          <Truck className="w-10 h-10 text-white" />
                        </motion.div>
                      </div>
                    </motion.div>

                    {/* Flow Line 2 */}
                    <motion.div
                      initial={{ opacity: 0, scaleY: 0 }}
                      whileInView={{ opacity: 1, scaleY: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.9, duration: 0.8 }}
                      className="flex justify-center"
                    >
                      <div className="w-1 h-8 bg-gradient-to-b from-yellow-600 to-yellow-300 rounded-full"></div>
                    </motion.div>

                    {/* Step 3: Condition Assessment */}
                    <motion.div
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 1.1 }}
                      className="space-y-6"
                    >
                      <div className="flex items-center gap-6">
                        <div className="relative">
                          <div className="w-20 h-20 bg-green-600 rounded-2xl flex items-center justify-center relative overflow-hidden">
                            <Archive className="w-10 h-10 text-white" />
                            <div className="absolute top-3 left-2 w-3 h-1 bg-green-300 rounded"></div>
                            <div className="absolute top-5 left-2 w-3 h-1 bg-green-300 rounded"></div>
                            <div className="absolute top-3 right-2 w-3 h-1 bg-green-300 rounded"></div>
                            <div className="absolute top-5 right-2 w-3 h-1 bg-green-300 rounded"></div>
                          </div>

                          <div className="absolute -bottom-2 -right-6 flex gap-1">
                            <div className="flex flex-col gap-0.5">
                              <div className="w-4 h-3 bg-white border border-green-500 rounded-sm shadow-sm"></div>
                              <div className="w-4 h-3 bg-white border border-green-500 rounded-sm shadow-sm"></div>
                            </div>
                            <div className="flex flex-col gap-0.5">
                              <div className="w-4 h-3 bg-white border border-orange-500 rounded-sm shadow-sm"></div>
                              <div className="w-4 h-3 bg-white border border-red-500 rounded-sm shadow-sm"></div>
                            </div>
                          </div>
                        </div>

                        <div className="bg-white px-6 py-3 rounded-xl shadow-lg border-2 border-green-600/20">
                          <p className="font-bold text-green-600 text-lg">Condition Assessed</p>
                          <p className="text-sm text-gray-600">Signs sorted & restocked</p>
                          <div className="flex items-center gap-2 mt-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-xs text-gray-500">8 signs processed</span>
                          </div>
                        </div>
                      </div>

                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 1.3 }}
                        className="flex flex-wrap gap-3 ml-24"
                      >
                        <div className="flex items-center gap-2 text-green-800 border-2 bg-white px-3 py-2 rounded-lg">
                          <CheckCircle className="w-4 h-4" />
                          <span className="font-medium">6 Good</span>
                        </div>
                        <div className="flex items-center gap-2 text-orange-500 border-2 bg-white px-3 py-2 rounded-lg">
                          <Wrench className="w-4 h-4" />
                          <span className="font-medium">1 Repair</span>
                        </div>
                        <div className="flex items-center gap-2 text-red-600 border-2 bg-white px-3 py-2 rounded-lg">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="font-medium">1 Damaged</span>
                        </div>
                      </motion.div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Key Features of Removal Orders */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Effortless Removals with RealEsy
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                Manage all signage and asset removals effortlessly — from scheduling and
                verification to automatic restocking, all in one platform.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
              {keyFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  className="bg-white p-8 rounded-2xl border border-gray-200 group cursor-pointer"
                >
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-md border border-gray-200 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={feature.icon} size={34} alt={feature.title} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Removal Workflow Visualization */}
      <FeatureSection className="bg-white py-12 md:py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-4">
                Visual Order Lifecycle
              </h2>
              <p className="text-base md:text-lg text-gray-700 max-w-2xl mx-auto">
                Track every Removal from draft to completion with our comprehensive workflow system.
              </p>
            </div>

            <div className="relative">
              {/* Desktop Workflow */}
              <div className="hidden lg:flex items-center justify-between">
                {(
                  [
                    { status: "draft", icon: "removalWorkflowDraft" },
                    { status: "scheduled", icon: "removalWorkflowScheduled" },
                    { status: "inprogress", icon: "removalWorkflowInProgress" },
                    { status: "completed", icon: "removalWorkflowCompleted" },
                    { status: "restocked", icon: "removalWorkflowRestocked" },
                  ] as const
                ).map((step, index, steps) => (
                  <React.Fragment key={step.status}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.2 }}
                      className="flex flex-col items-center"
                    >
                      <div className="w-16 h-16 bg-white rounded-2xl border border-gray-200 shadow-md flex items-center justify-center mb-4">
                        <AppIcon name={step.icon} size={36} alt={step.status} />
                      </div>
                      <StatusBadge status={step.status} delay={index * 0.2 + 0.3} />
                    </motion.div>

                    {index < steps.length - 1 && (
                      <motion.div
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 1 + index * 0.3, duration: 0.8 }}
                        className="flex-1 h-1 bg-[#236DD9] rounded-full mx-4"
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Mobile Workflow */}
              <div className="lg:hidden space-y-8">
                {(
                  [
                    {
                      status: "draft",
                      icon: "removalWorkflowDraft",
                      desc: "Request created and ready for scheduling",
                    },
                    {
                      status: "scheduled",
                      icon: "removalWorkflowScheduled",
                      desc: "Pickup date and time confirmed",
                    },
                    {
                      status: "inprogress",
                      icon: "removalWorkflowInProgress",
                      desc: "Installer on-site removing signage",
                    },
                    {
                      status: "completed",
                      icon: "removalWorkflowCompleted",
                      desc: "Signs removed with photo verification",
                    },
                    {
                      status: "restocked",
                      icon: "removalWorkflowRestocked",
                      desc: "Signs assessed and restocked in inventory",
                    },
                  ] as const
                ).map((step, index) => (
                  <motion.div
                    key={step.status}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-12 h-12 bg-white rounded-xl border border-gray-200 shadow-md flex items-center justify-center flex-shrink-0">
                      <AppIcon name={step.icon} size={28} alt={step.status} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <StatusBadge status={step.status} delay={index * 0.1 + 0.2} />
                      </div>
                      <p className="text-sm text-gray-600">{step.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Work on the Go with Mobile App Section */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left side: Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-8"
              >
                <div>
                  <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                    Work on the Go with Our Mobile App
                  </h2>
                  <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-8">
                    Never wait until you're back at the office. With the RealEsy Mobile App, you can
                    manage removals, track progress, and approve orders from anywhere.
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {(
                      [
                        {
                          icon: "createRemovalOrderFromAnywhere",
                          title: "Create removal orders from anywhere",
                          description: "Quick removal requests on-the-go",
                        },
                        {
                          icon: "trackRemovalLiveProgressIndicator",
                          title: "Track removals live with progress indicators",
                          description: "Real-time status updates",
                        },
                        {
                          icon: "reviewPhotoProofInstantly",
                          title: "Review photo proof instantly",
                          description: "Before/after verification",
                        },
                        {
                          icon: "approveOrScheduleJobQuickAction",
                          title: "Approve or reschedule jobs with Quick Actions",
                          description: "One-tap job management",
                        },
                        {
                          icon: "getNotifiedAssetsRestocked",
                          title: "Get notified when assets are restocked",
                          description: "Automatic inventory updates",
                        },
                        {
                          icon: "monitorConditionAssessment",
                          title: "Monitor condition assessments",
                          description: "Track asset recovery rates",
                        },
                      ] as const
                    ).map((feature, index) => (
                      <motion.div
                        key={feature.title}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 + index * 0.1 }}
                        className="bg-gray-50 p-6 rounded-2xl border border-gray-200 group hover:shadow-lg transition-all duration-300"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-white rounded-xl shadow-md border border-gray-200 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                            <AppIcon name={feature.icon} size={28} alt={feature.title} />
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                            <p className="text-sm text-gray-600 leading-relaxed">
                              {feature.description}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 pt-4">
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 w-full sm:w-auto"
                  >
                    Download the App
                    <Download className="ml-3 w-5 h-5" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 px-8 py-6 text-base font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 w-full sm:w-auto"
                  >
                    Request a Mobile Demo
                    <ArrowRight className="ml-3 w-5 h-5" />
                  </Button>
                </div>
              </motion.div>

              {/* Right side: Mobile App Mockup */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="lg:pl-8"
              >
                <div className="relative max-w-[360px] mx-auto">
                  {/* Enhanced Background Effects */}
                  <div className="absolute -inset-12 opacity-20">
                    <div className="absolute top-8 left-8 w-24 h-24 bg-blue-400 rounded-full blur-2xl animate-pulse"></div>
                    <div
                      className="absolute bottom-8 right-8 w-28 h-28 bg-purple-400 rounded-full blur-2xl animate-pulse"
                      style={{ animationDelay: "1s" }}
                    ></div>
                    <div
                      className="absolute top-1/2 left-2 w-20 h-20 rounded-full blur-2xl animate-pulse"
                      style={{ background: "#f58b01", animationDelay: "2s" }}
                    ></div>
                  </div>

                  {/* iPhone 17 Pro Simulator Frame */}
                  <motion.div
                    whileHover={{ y: -10, rotateY: 8, rotateX: 5 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="relative transform-gpu mx-auto w-full max-w-[360px]"
                    style={{ perspective: "1000px" }}
                  >
                    {/* Left side: Action / Silent + Volume Up / Volume Down */}
                    <div
                      className="absolute left-0 top-[88px] -translate-x-[3px] w-[3px] h-[22px] bg-[#2c2c2e] rounded-l-[2px] z-30"
                      aria-hidden="true"
                    />
                    <div
                      className="absolute left-0 top-[140px] -translate-x-[3px] w-[3px] h-[40px] bg-[#2c2c2e] rounded-l-[2px] z-30"
                      aria-hidden="true"
                    />
                    <div
                      className="absolute left-0 top-[190px] -translate-x-[3px] w-[3px] h-[40px] bg-[#2c2c2e] rounded-l-[2px] z-30"
                      aria-hidden="true"
                    />

                    {/* Right side: Power / Side button */}
                    <div
                      className="absolute right-0 top-[160px] translate-x-[3px] w-[3px] h-[64px] bg-[#2c2c2e] rounded-r-[2px] z-30"
                      aria-hidden="true"
                    />

                    <div className="relative bg-[#0b0b0c] rounded-[3.1rem] p-[11px] shadow-[0_25px_60px_rgba(0,0,0,0.35)] transform-gpu ring-1 ring-black/40">
                      <div className="relative bg-[#1a1a1c] rounded-[2.65rem] p-[2px]">
                        {/* Phone Screen */}
                        <div className="bg-[#f5f5f7] rounded-[2.55rem] overflow-hidden relative min-h-[720px]">
                          {/* Status Bar + Dynamic Island */}
                          <div className="relative px-7 pt-4 pb-2.5 flex justify-between items-center z-10">
                            <span className="text-[13px] font-semibold text-gray-900 tracking-tight">
                              9:41
                            </span>
                            <div className="absolute left-1/2 top-2.5 -translate-x-1/2 w-[102px] h-[30px] bg-black rounded-full z-20 flex items-center justify-end pr-3">
                              <motion.div
                                animate={{ scale: [1, 1.15, 1] }}
                                transition={{ duration: 3, repeat: Infinity }}
                                className="w-2 h-2 rounded-full bg-[#1a1a1a] ring-1 ring-gray-700"
                              />
                            </div>
                            <div className="flex items-center gap-1.5 text-gray-900">
                              <svg width="16" height="12" viewBox="0 0 16 12" fill="currentColor" aria-hidden="true">
                                <rect x="0" y="8" width="2.5" height="4" rx="0.5" />
                                <rect x="4" y="5" width="2.5" height="7" rx="0.5" />
                                <rect x="8" y="2" width="2.5" height="10" rx="0.5" />
                                <rect x="12" y="0" width="2.5" height="12" rx="0.5" />
                              </svg>
                              <svg width="15" height="11" viewBox="0 0 15 11" fill="currentColor" aria-hidden="true">
                                <path d="M7.5 2.2c1.9 0 3.6.7 4.9 1.9l-.9.9A5.3 5.3 0 0 0 7.5 3.5 5.3 5.3 0 0 0 3.5 5l-.9-.9A6.9 6.9 0 0 1 7.5 2.2zm0 2.4c1.2 0 2.3.5 3.1 1.2l-.9.9a2.9 2.9 0 0 0-4.4 0l-.9-.9A4.3 4.3 0 0 1 7.5 4.6zM7.5 9.2a1.1 1.1 0 1 0 0-2.2 1.1 1.1 0 0 0 0 2.2z" />
                              </svg>
                              <svg width="24" height="12" viewBox="0 0 24 12" fill="none" aria-hidden="true">
                                <rect x="0.5" y="0.5" width="20" height="11" rx="2.5" stroke="currentColor" strokeOpacity="0.35" />
                                <rect x="2" y="2" width="15" height="8" rx="1.5" fill="currentColor" />
                                <path d="M22 4v4a1.5 1.5 0 0 0 0-4z" fill="currentColor" fillOpacity="0.4" />
                              </svg>
                            </div>
                          </div>

                          <div className="px-5 pb-8 pt-4 space-y-4 bg-[#f5f5f7] min-h-[640px]">
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.5 }}
                              className="flex items-center justify-between p-5 bg-white rounded-2xl shadow-sm border border-gray-100 mt-14"
                            >
                              <div>
                                <h3 className="font-bold text-[17px] text-gray-900 leading-tight">
                                  Removal Orders
                                </h3>
                                <div className="flex items-center gap-2 mt-1.5">
                                  <motion.div
                                    animate={{ scale: [1, 1.2, 1] }}
                                    transition={{ duration: 1.5, repeat: Infinity }}
                                    className="w-2 h-2 bg-green-500 rounded-full"
                                  />
                                  <p className="text-[13px] text-gray-500">8 active removals</p>
                                </div>
                              </div>
                              <div
                                className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md"
                                style={{ background: "#236DD9" }}
                              >
                                <Trash2 className="w-6 h-6 text-white" />
                              </div>
                            </motion.div>

                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.7 }}
                              className="grid grid-cols-2 gap-4"
                            >
                              <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                                <div className="text-[28px] font-bold text-blue-600 leading-none mb-1.5">
                                  18
                                </div>
                                <div className="text-[13px] text-blue-600">Active</div>
                              </div>
                              <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                                <div className="text-[28px] font-bold text-green-600 leading-none mb-1.5">
                                  134
                                </div>
                                <div className="text-[13px] text-green-600">Recovered</div>
                              </div>
                            </motion.div>

                            <div className="space-y-3.5">
                              <h4 className="font-bold text-[15px] text-gray-900 px-0.5">
                                Recent Removals
                              </h4>
                              {[
                                {
                                  address: "1234 Beverly Hills Dr",
                                  status: "scheduled",
                                  time: "Tomorrow 11 AM",
                                  color: "bg-purple-100 text-purple-800",
                                },
                                {
                                  address: "5678 Manhattan Beach",
                                  status: "pickup",
                                  time: "In progress",
                                  color: "bg-yellow-100 text-yellow-800",
                                },
                                {
                                  address: "9101 Santa Monica",
                                  status: "completed",
                                  time: "Good condition",
                                  color: "bg-green-100 text-green-800",
                                },
                              ].map((order, index) => (
                                <motion.div
                                  key={index}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.9 + index * 0.1 }}
                                  className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm"
                                >
                                  <div className="flex items-center gap-3.5">
                                    <Trash2 className="w-5 h-5 text-red-500 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-bold text-gray-900 truncate">
                                        {order.address}
                                      </p>
                                      <p className="text-xs text-gray-500">{order.time}</p>
                                    </div>
                                    <div className="px-2.5 py-1 rounded-full text-[11px] font-medium text-gray-700 bg-gray-100 whitespace-nowrap flex-shrink-0">
                                      {order.status}
                                    </div>
                                  </div>
                                </motion.div>
                              ))}
                            </div>

                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 1.3 }}
                              className="grid grid-cols-2 gap-3.5 pt-2"
                            >
                              <button
                                className="text-white p-4 rounded-2xl flex items-center justify-center gap-2 text-sm font-semibold shadow-md"
                                style={{ background: "#236DD9" }}
                              >
                                <ClipboardList className="w-4 h-4" />
                                New Removal
                              </button>
                              <button className="bg-white border border-gray-200 text-gray-600 p-4 rounded-2xl flex items-center justify-center gap-2 text-sm font-semibold shadow-sm">
                                <Camera className="w-4 h-4" />
                                View Photos
                              </button>
                            </motion.div>
                          </div>

                          {/* Home Indicator */}
                          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[110px] h-[4px] bg-black/80 rounded-full" />
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ delay: 1.5, duration: 0.5 }}
                    className="absolute -right-16 top-16 bg-white rounded-2xl shadow-2xl p-3 border-2 max-w-48 z-20"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <Archive className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">Sign Restocked!</p>
                        {/* <p className="text-xs text-gray-600">Condition: Good ✓</p> */}
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Benefits Section */}
      <FeatureSection className="bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Brokerages Choose RealEsy for Removals
              </h2>
              <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                By combining proof, inventory intelligence, and cost savings, RealEsy ensures
                removal jobs are no longer an afterthought. Brokerages recover more signs, reduce
                unnecessary purchases, and keep agents focused on selling — not chasing signage.
              </p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="mt-8"
              >
                <p className="text-xl font-bold text-blue-600">
                  RealEsy turns removals into a value-generating process instead of a cost sink.
                </p>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -10, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  className="bg-white p-6 rounded-2xl border border-gray-200 text-center group cursor-pointer"
                >
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-md border border-gray-200 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <AppIcon name={benefit.icon} size={36} alt={benefit.title} />
                  </div>
                  <h3 className="font-bold text-lg mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* FAQ Section */}
      <FeatureSection className="bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-lg md:text-xl text-gray-700">
                Get answers to common questions about our removal order system.
              </p>
            </div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 p-8 rounded-2xl border border-gray-200"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <AppIcon name="faqQuestion" size={28} alt="FAQ" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-900 mb-3">{faq.q}</h3>
                      <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Enhanced Final CTA */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 py-24 overflow-hidden">
        <motion.div style={{ y: backgroundY }} className="absolute inset-0 opacity-20">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 1, 0],
                scale: [0, 1, 0],
                x: Math.random() * 800,
                y: Math.random() * 600,
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                delay: i * 0.8,
                ease: "easeInOut",
              }}
              className="absolute w-8 h-8"
            >
              {i % 2 === 0 ? (
                <Trash2 className="w-full h-full text-white" />
              ) : (
                <div className="w-full h-full bg-white rounded-full opacity-60"></div>
              )}
            </motion.div>
          ))}
        </motion.div>

        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Stop losing signs — start saving money.
            </h2>
            <p className="text-lg md:text-xl text-blue-100 mb-12 leading-relaxed">
              RealEsy makes removals seamless, verifiable, and cost-effective.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center opacity-100">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-white text-blue-700 hover:bg-blue-50 hover:text-blue-800 px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg font-bold rounded-xl shadow-2xl hover:shadow-3xl transform hover:scale-[1.02] transition-all duration-300 border-2 border-white opacity-100 w-full sm:w-auto"
                >
                  Get Started
                  <ArrowRight className="ml-3 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
